import { getApiUrl } from "@/utils/api";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Save, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { WysiwygEditor } from "./WysiwygEditor";

const API = getApiUrl();

const PAGE_LABELS = {
  home: "Home Page",
  navigation: "Navigation Menu",
  properties: "Properties Page",
  property: "Property Detail Page",
  contact: "Contact Page",
  joinus: "Join Us / Become a Partner Page",
  terms: "Terms and Conditions Page",
  privacy: "Privacy Page",
  about: "About Page",
  howitworks: "How It Works Page",
  prices: "Prices Page",
  footer: "Footer",
  common: "Common Labels",
  // New Yopa-inspired pages
  valuation: "Free Valuation Page",
  "online-valuation": "Online Valuation Page",
  testimonials: "Testimonials Page",
  guides: "Guides Page",
  blog: "Blog Page",
  "mortgage-calculator": "Mortgage Calculator Page",
  "moving-cost-calculator": "Moving Cost Calculator Page",
  "our-agents": "Our Agents Page",
  "request-callback": "Request Callback Page"
};

// Map page IDs to their actual URL paths
const PAGE_PATHS = {
  home: "/",
  properties: "/properties",
  property: "/properties", // Property detail is dynamic, link to list
  contact: "/contact",
  joinus: "/join-us",
  terms: "/terms",
  privacy: "/privacy",
  about: "/about",
  howitworks: "/how-it-works",
  prices: "/prices",
  valuation: "/valuation",
  "online-valuation": "/online-valuation",
  testimonials: "/testimonials",
  guides: "/guides",
  blog: "/blog",
  "mortgage-calculator": "/mortgage-calculator",
  "moving-cost-calculator": "/moving-cost-calculator",
  "our-agents": "/our-agents",
  "request-callback": "/request-callback",
  // Navigation, footer, common have no direct page links
  navigation: null,
  footer: null,
  common: null
};

const SECTION_LABELS = {
  // Home
  hero_subtitle: "Hero Subtitle",
  hero_title: "Hero Title",
  hero_description: "Hero Description",
  hero_cta: "Hero Button Text",
  featured_subtitle: "Featured Section Subtitle",
  featured_title: "Featured Section Title",
  cta_title: "CTA Section Title",
  cta_description: "CTA Section Description",
  cta_button: "CTA Button Text",
  stat_1_value: "Stat 1 Value",
  stat_1_label: "Stat 1 Label",
  stat_2_value: "Stat 2 Value",
  stat_2_label: "Stat 2 Label",
  stat_3_value: "Stat 3 Value",
  stat_3_label: "Stat 3 Label",
  // Navigation
  nav_home: "Home Link",
  nav_properties: "Properties Link",
  nav_contact: "Contact Link",
  nav_cta: "Nav CTA Button",
  // Properties Page
  page_subtitle: "Page Subtitle",
  page_title: "Page Title",
  page_description: "Page Description",
  view_details_btn: "View Details Button",
  no_results: "No Results Message",
  filter_location: "Location Filter Label",
  filter_beds: "Beds Filter Label",
  filter_price_min: "Min Price Label",
  filter_price_max: "Max Price Label",
  // Property Detail
  back_link: "Back Link Text",
  price_label: "Price Label",
  enquire_btn: "Enquire Button",
  call_text: "Call Text",
  about_title: "About Section Title",
  features_title: "Features Section Title",
  gallery_title: "Gallery Section Title",
  floorplan_title: "Floorplan Section Title",
  location_title: "Location Section Title",
  brochure_title: "Brochure Section Title",
  brochure_desc: "Brochure Description",
  download_btn: "Download Button Text",
  featured_by: "Featured By Label",
  share_label: "Share Label",
  share_facebook_cta: "Facebook Share Text",
  share_twitter_cta: "Twitter/X Share Text",
  share_linkedin_cta: "LinkedIn Share Text",
  share_whatsapp_cta: "WhatsApp Share Text",
  share_copy_cta: "Copy Link Toast Text",
  // Contact Page
  form_title: "Form Title",
  form_description: "Form Description",
  form_name_label: "Name Field Label",
  form_email_label: "Email Field Label",
  form_phone_label: "Phone Field Label",
  form_message_label: "Message Field Label",
  form_submit_btn: "Submit Button Text",
  // Footer
  tagline: "Company Tagline",
  quick_links_title: "Quick Links Title",
  contact_title: "Contact Section Title",
  email: "Contact Email",
  phone: "Contact Phone",
  address: "Address",
  copyright: "Copyright Text",
  // Common Labels
  bedrooms: "Bedrooms Label",
  bathrooms: "Bathrooms Label",
  type: "Type Label",
  tenure: "Tenure Label",
  size: "Size Label",
  parking: "Parking Label",
  garden_faces: "Garden Faces Label",
  stations: "Stations Label",
  featured_badge: "Featured Badge",
  video_badge: "Video Badge",
  // Join Us Page
  benefits_title: "Benefits Section Title",
  benefit_1: "Benefit 1",
  benefit_2: "Benefit 2",
  benefit_3: "Benefit 3",
  benefit_4: "Benefit 4",
  agency_name_label: "Agency Name Label",
  contact_name_label: "Contact Name Label",
  email_label: "Email Label",
  phone_label: "Phone Label",
  website_label: "Website Label",
  message_label: "Message Label",
  submit_btn: "Submit Button Text",
  enquire_cta: "Enquire CTA Button",
  // Terms Page
  page_content: "Terms Content (HTML)",
  last_updated: "Last Updated Text",
  // How It Works Page
  hiw_hero_subtitle: "Hero Subtitle",
  hiw_hero_title: "Hero Title",
  hiw_hero_description: "Hero Description",
  hiw_phase1_title: "Phase 1 Title",
  hiw_phase1_subtitle: "Phase 1 Subtitle",
  hiw_phase2_title: "Phase 2 Title",
  hiw_phase2_subtitle: "Phase 2 Subtitle",
  hiw_phase3_title: "Phase 3 Title",
  hiw_phase3_subtitle: "Phase 3 Subtitle",
  hiw_cta_title: "CTA Title",
  hiw_cta_description: "CTA Description",
  // Privacy
  privacy_title: "Page Title", privacy_last_updated: "Last Updated",
  privacy_intro_title: "Intro Heading", privacy_intro: "Intro Text",
  privacy_collect_title: "Collection Heading", privacy_collect: "Collection Text",
  privacy_use_title: "Usage Heading", privacy_use: "Usage Text",
  privacy_cookies_title: "Cookies Heading", privacy_cookies: "Cookies Text",
  privacy_rights_title: "Rights Heading", privacy_rights: "Rights Text",
  privacy_retention_title: "Retention Heading", privacy_retention: "Retention Text",
  privacy_contact_title: "Contact Heading", privacy_contact: "Contact Text",
  // About
  about_title: "Page Title", about_seo_title: "SEO Title", about_seo_summary: "SEO Summary",
  about_mission_title: "Mission Heading", about_mission: "Mission Text",
  about_features_title: "Features Section Heading",
  feature_1_title: "Feature 1 Title", feature_1_desc: "Feature 1 Description",
  feature_2_title: "Feature 2 Title", feature_2_desc: "Feature 2 Description",
  feature_3_title: "Feature 3 Title", feature_3_desc: "Feature 3 Description",
  feature_4_title: "Feature 4 Title", feature_4_desc: "Feature 4 Description",
  feature_5_title: "Feature 5 Title", feature_5_desc: "Feature 5 Description",
  feature_6_title: "Feature 6 Title", feature_6_desc: "Feature 6 Description",
  feature_7_title: "Feature 7 Title", feature_7_desc: "Feature 7 Description",
  feature_8_title: "Feature 8 Title", feature_8_desc: "Feature 8 Description",
  about_cta_title: "CTA Title", about_cta_desc: "CTA Description",
  hiw_prep_step1_title: "Step 1 Title", hiw_prep_step1_desc: "Step 1 Description",
  hiw_prep_step2_title: "Step 2 Title", hiw_prep_step2_desc: "Step 2 Description",
  hiw_prep_step3_title: "Step 3 Title", hiw_prep_step3_desc: "Step 3 Description",
  hiw_prep_step4_title: "Step 4 Title", hiw_prep_step4_desc: "Step 4 Description",
  hiw_prep_step5_title: "Step 5 Title", hiw_prep_step5_desc: "Step 5 Description",
  hiw_launch_step1_title: "Step 1 Title", hiw_launch_step1_desc: "Step 1 Description",
  hiw_launch_step2_title: "Step 2 Title", hiw_launch_step2_desc: "Step 2 Description",
  hiw_launch_step3_title: "Step 3 Title", hiw_launch_step3_desc: "Step 3 Description",
  hiw_launch_step4_title: "Step 4 Title", hiw_launch_step4_desc: "Step 4 Description",
  hiw_launch_step5_title: "Step 5 Title", hiw_launch_step5_desc: "Step 5 Description",
  hiw_monitor_step1_title: "Step 1 Title", hiw_monitor_step1_desc: "Step 1 Description",
  hiw_monitor_step2_title: "Step 2 Title", hiw_monitor_step2_desc: "Step 2 Description",
  hiw_monitor_step3_title: "Step 3 Title", hiw_monitor_step3_desc: "Step 3 Description",
  // Prices Page
  prices_hero_subtitle: "Hero Subtitle",
  prices_hero_title: "Hero Title",
  prices_hero_description: "Hero Description",
  prices_setup_title: "Initial Setup Title",
  prices_setup_price: "Initial Setup Price",
  prices_setup_description: "Initial Setup Description",
  prices_monthly_title: "Monthly Campaign Title",
  prices_monthly_price: "Monthly Campaign Price",
  prices_monthly_description: "Monthly Campaign Description",
  prices_video_title: "Videography Title",
  prices_video_price: "Videography Price",
  prices_video_description: "Videography Description",
  prices_cta_title: "CTA Title",
  prices_cta_description: "CTA Description"
};

// Group sections by category for better organization
const SECTION_GROUPS = {
  home: {
    "Hero Section": ["hero_subtitle", "hero_title", "hero_description", "hero_cta"],
    "Featured Section": ["featured_subtitle", "featured_title"],
    "CTA Section": ["cta_title", "cta_description", "cta_button"],
    "Statistics": ["stat_1_value", "stat_1_label", "stat_2_value", "stat_2_label", "stat_3_value", "stat_3_label"]
  },
  navigation: {
    "Navigation Links": ["nav_home", "nav_properties", "nav_contact", "nav_cta"]
  },
  properties: {
    "Page Header": ["page_subtitle", "page_title", "page_description"],
    "Buttons & Messages": ["view_details_btn", "no_results"],
    "Filter Labels": ["filter_location", "filter_beds", "filter_price_min", "filter_price_max"]
  },
  property: {
    "Navigation": ["back_link"],
    "Price & Actions": ["price_label", "enquire_btn", "call_text"],
    "Section Titles": ["about_title", "features_title", "gallery_title", "floorplan_title", "location_title"],
    "Brochure": ["brochure_title", "brochure_desc", "download_btn"],
    "Agent & Share": ["featured_by", "share_label"],
    "Social Share CTAs": ["share_facebook_cta", "share_twitter_cta", "share_linkedin_cta", "share_whatsapp_cta", "share_copy_cta"]
  },
  contact: {
    "Page Header": ["page_subtitle", "page_title", "page_description"],
    "Form Header": ["form_title", "form_description"],
    "Form Labels": ["form_name_label", "form_email_label", "form_phone_label", "form_message_label", "form_submit_btn"]
  },
  joinus: {
    "Page Header": ["page_subtitle", "page_title", "page_description"],
    "Benefits Section": ["benefits_title", "benefit_1", "benefit_2", "benefit_3", "benefit_4"],
    "Form Header": ["form_title", "form_description"],
    "Form Labels": ["agency_name_label", "contact_name_label", "email_label", "phone_label", "website_label", "message_label"],
    "Buttons": ["submit_btn", "enquire_cta"]
  },
  terms: {
    "Page Header": ["page_title", "last_updated"],
    "Content": ["page_content"]
  },
  footer: {
    "Company Info": ["tagline", "copyright"],
    "Section Titles": ["quick_links_title", "contact_title"],
    "Contact Details": ["email", "phone", "address"]
  },
  common: {
    "Property Labels": ["bedrooms", "bathrooms", "type", "tenure", "size", "parking", "garden_faces", "stations"],
    "Badges": ["featured_badge", "video_badge"]
  },
  privacy: {
    "Page Header": ["privacy_title", "privacy_last_updated"],
    "Introduction": ["privacy_intro_title", "privacy_intro"],
    "Information We Collect": ["privacy_collect_title", "privacy_collect"],
    "How We Use Info": ["privacy_use_title", "privacy_use"],
    "Cookies": ["privacy_cookies_title", "privacy_cookies"],
    "Your Rights": ["privacy_rights_title", "privacy_rights"],
    "Data Retention": ["privacy_retention_title", "privacy_retention"],
    "Contact": ["privacy_contact_title", "privacy_contact"]
  },
  about: {
    "Page Header & SEO": ["about_title", "about_seo_title", "about_seo_summary"],
    "Mission": ["about_mission_title", "about_mission"],
    "Features Section": ["about_features_title"],
    "Feature 1": ["feature_1_title", "feature_1_desc"],
    "Feature 2": ["feature_2_title", "feature_2_desc"],
    "Feature 3": ["feature_3_title", "feature_3_desc"],
    "Feature 4": ["feature_4_title", "feature_4_desc"],
    "Feature 5": ["feature_5_title", "feature_5_desc"],
    "Feature 6": ["feature_6_title", "feature_6_desc"],
    "Feature 7": ["feature_7_title", "feature_7_desc"],
    "Feature 8": ["feature_8_title", "feature_8_desc"],
    "CTA": ["about_cta_title", "about_cta_desc"]
  },
  howitworks: {
    "Hero Section": ["hiw_hero_subtitle", "hiw_hero_title", "hiw_hero_description"],
    "Phase 1 - Preparation": ["hiw_phase1_title", "hiw_phase1_subtitle"],
    "Preparation Steps": ["hiw_prep_step1_title", "hiw_prep_step1_desc", "hiw_prep_step2_title", "hiw_prep_step2_desc", "hiw_prep_step3_title", "hiw_prep_step3_desc", "hiw_prep_step4_title", "hiw_prep_step4_desc", "hiw_prep_step5_title", "hiw_prep_step5_desc"],
    "Phase 2 - Campaign": ["hiw_phase2_title", "hiw_phase2_subtitle"],
    "Campaign Steps": ["hiw_launch_step1_title", "hiw_launch_step1_desc", "hiw_launch_step2_title", "hiw_launch_step2_desc", "hiw_launch_step3_title", "hiw_launch_step3_desc", "hiw_launch_step4_title", "hiw_launch_step4_desc", "hiw_launch_step5_title", "hiw_launch_step5_desc"],
    "Phase 3 - Monitor": ["hiw_phase3_title", "hiw_phase3_subtitle"],
    "Monitor Steps": ["hiw_monitor_step1_title", "hiw_monitor_step1_desc", "hiw_monitor_step2_title", "hiw_monitor_step2_desc", "hiw_monitor_step3_title", "hiw_monitor_step3_desc"],
    "CTA Section": ["hiw_cta_title", "hiw_cta_description"]
  },
  prices: {
    "Hero Section": ["prices_hero_subtitle", "prices_hero_title", "prices_hero_description"],
    "Initial Setup": ["prices_setup_title", "prices_setup_price", "prices_setup_description"],
    "Monthly Campaign": ["prices_monthly_title", "prices_monthly_price", "prices_monthly_description"],
    "Videography Add-on": ["prices_video_title", "prices_video_price", "prices_video_description"],
    "CTA Section": ["prices_cta_title", "prices_cta_description"]
  }
};

export const ContentTab = ({ pageContent, setPageContent }) => {
  const [contentSaving, setContentSaving] = useState(false);
  const [savingSection, setSavingSection] = useState(null);

  const handleContentChange = (pageId, sectionId, value) => {
    setPageContent(prev => ({
      ...prev,
      [pageId]: prev[pageId].map(item =>
        item.section_id === sectionId
          ? { ...item, content: value }
          : item
      )
    }));
  };

  const handleSaveContent = async (pageId, sectionId, content) => {
    setContentSaving(true);
    setSavingSection(`${pageId}-${sectionId}`);
    try {
      await axios.put(
        `${API}/admin/content/${pageId}/${sectionId}`,
        { content },
        { withCredentials: true }
      );
      toast.success("Content saved successfully!");
    } catch (error) {
      console.error("Error saving content:", error);
      toast.error("Failed to save content");
    } finally {
      setContentSaving(false);
      setSavingSection(null);
    }
  };

  const handleSaveAllPageContent = async (pageId) => {
    setContentSaving(true);
    try {
      const sections = pageContent[pageId] || [];
      for (const item of sections) {
        await axios.put(
          `${API}/admin/content/${pageId}/${item.section_id}`,
          { content: item.content },
          { withCredentials: true }
        );
      }
      toast.success(`All ${PAGE_LABELS[pageId] || pageId} content saved!`);
    } catch (error) {
      console.error("Error saving page content:", error);
      toast.error("Failed to save content");
    } finally {
      setContentSaving(false);
    }
  };

  // Organize sections by group
  const getGroupedSections = (pageId, sections) => {
    const groups = SECTION_GROUPS[pageId];
    if (!groups) {
      // Fallback: return all sections in one group
      return { "All Sections": sections };
    }

    const grouped = {};
    const sectionMap = {};
    sections.forEach(s => { sectionMap[s.section_id] = s; });

    for (const [groupName, sectionIds] of Object.entries(groups)) {
      const groupSections = sectionIds
        .filter(id => sectionMap[id])
        .map(id => sectionMap[id]);
      if (groupSections.length > 0) {
        grouped[groupName] = groupSections;
      }
    }

    return grouped;
  };

  return (
    <div className="space-y-6" data-testid="content-tab">
      <div>
        <h2 className="font-serif text-2xl font-medium">Page Content</h2>
        <p className="text-muted-foreground mt-1">Edit text, labels, and buttons across your website</p>
      </div>

      {/* Sticky Mini Navigation */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border -mx-6 px-6 py-3">
        <div className="flex flex-wrap gap-2">
          {Object.keys(pageContent).map((pageId) => (
            <button
              key={pageId}
              onClick={() => {
                const element = document.getElementById(`content-section-${pageId}`);
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              className="px-3 py-1.5 text-sm font-medium rounded-full border border-border hover:bg-primary hover:text-white hover:border-primary transition-colors"
              data-testid={`nav-${pageId}`}
            >
              {PAGE_LABELS[pageId] || pageId}
            </button>
          ))}
        </div>
      </div>

      {Object.entries(pageContent).map(([pageId, sections]) => {
        const groupedSections = getGroupedSections(pageId, sections);
        
        return (
          <div 
            key={pageId} 
            id={`content-section-${pageId}`}
            className="bg-white border border-border overflow-hidden scroll-mt-20" 
            data-testid={`content-page-${pageId}`}
          >
            {/* Page Header */}
            <div className="bg-muted/30 border-b border-border px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h3 className="font-serif text-xl font-medium">
                  {PAGE_LABELS[pageId] || pageId}
                </h3>
                {PAGE_PATHS[pageId] && (
                  <a
                    href={`${window.location.origin}${PAGE_PATHS[pageId]}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:text-primary/80 transition-colors flex items-center gap-1 text-sm"
                    title="View live page"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span>View Live</span>
                  </a>
                )}
              </div>
              <Button
                onClick={() => handleSaveAllPageContent(pageId)}
                disabled={contentSaving}
                size="sm"
                className="bg-primary text-white hover:bg-primary/90 rounded-none"
              >
                {contentSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save All
              </Button>
            </div>

            {/* Grouped Sections */}
            <div className="p-6 space-y-6">
              {Object.entries(groupedSections).map(([groupName, groupSections]) => (
                <div key={groupName} className="space-y-4">
                  <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide border-b border-border pb-2">
                    {groupName}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {groupSections.map((item) => (
                      <div key={item.section_id} className={`space-y-2 ${item.content_type === 'richtext' ? 'col-span-2' : ''}`}>
                        <Label className="text-sm font-medium">
                          {SECTION_LABELS[item.section_id] || item.section_id}
                        </Label>
                        <div className="flex gap-2">
                          {item.content_type === "richtext" ? (
                            <div className="flex-1">
                              {/* Inline WYSIWYG Editor for Terms and Conditions */}
                              <WysiwygEditor
                                value={item.content}
                                onSave={(content) => handleSaveContent(pageId, item.section_id, content)}
                                placeholder="Enter Terms and Conditions content..."
                              />
                            </div>
                          ) : item.content_type === "text" && item.content.length > 50 ? (
                            <Textarea
                              value={item.content}
                              onChange={(e) => handleContentChange(pageId, item.section_id, e.target.value)}
                              className="rounded-none min-h-20"
                              data-testid={`content-${pageId}-${item.section_id}`}
                            />
                          ) : (
                            <Input
                              value={item.content}
                              onChange={(e) => handleContentChange(pageId, item.section_id, e.target.value)}
                              className="rounded-none"
                              data-testid={`content-${pageId}-${item.section_id}`}
                            />
                          )}
                          {item.content_type !== "richtext" && (
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleSaveContent(pageId, item.section_id, item.content)}
                              disabled={contentSaving && savingSection === `${pageId}-${item.section_id}`}
                              className="rounded-none shrink-0"
                              data-testid={`save-${pageId}-${item.section_id}`}
                            >
                              {contentSaving && savingSection === `${pageId}-${item.section_id}` ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Save size={16} />
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      
      {/* Rich Text Editor Dialog */}
    </div>
  );
};

export default ContentTab;
